import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API from '../api';
import './loadingAnimation.css';
import { SEMANTIC, UI } from '../styles/theme';
import BackButton from '../components/common/BackButton';

const HOLD_COLOR = '#FFD166'; // giallo HOLD (safe)

// ─── Neural-net loading animation ────────────────────────────────────────────
// Rete neurale: 2 input gialli → 3+3 hidden blu → 1 output verde
// I "pulse" scorrono in sequenza per layer in un ciclo di 3 s.
const CYCLE = 3; // durata ciclo (s)
const PULSE_DUR = 0.55; // durata singolo pulse (s)

function NeuralNetLoader() {
  const W = 440, H = 280, R = 20, PR = 6;

  // Coordinate nodi
  const inp = [[65, 105], [65, 175]];
  const h1  = [[180, 62], [180, 140], [180, 218]];
  const h2  = [[270, 62], [270, 140], [270, 218]];
  const out = [[380, 140]];

  // Colori
  const COL_Y = '#FFB800'; // giallo
  const COL_B = '#3A9FE8'; // blu
  const COL_G = '#3DB85A'; // verde

  // Edge list: { id, x1,y1, x2,y2, layer (0|1|2) }
  const edges = [];
  let eid = 0;
  const addLayer = (from, to, layer) =>
    from.forEach(f => to.forEach(t => edges.push({ id: eid++, x1: f[0], y1: f[1], x2: t[0], y2: t[1], layer })));
  addLayer(inp, h1, 0);
  addLayer(h1, h2, 1);
  addLayer(h2, out, 2);

  // Timing helper: restituisce keyTimes/keyPoints per animateMotion a 3 s
  // pulse appare da startT a startT+PULSE_DUR
  const pulseKeys = (startT) => {
    const s = startT / CYCLE;
    const e = (startT + PULSE_DUR) / CYCLE;
    return {
      keyTimes: `0;${s.toFixed(4)};${e.toFixed(4)};${Math.min(e + 0.001, 1).toFixed(4)};1`,
      keyPoints: `0;0;1;1;1`,
    };
  };
  const pulseOpacity = (startT) => {
    const s = startT / CYCLE;
    const e = (startT + PULSE_DUR) / CYCLE;
    const fi = Math.min(s + 0.04, e); // fade-in end
    const fo = Math.max(e - 0.04, s); // fade-out start
    return {
      keyTimes: `0;${s.toFixed(4)};${fi.toFixed(4)};${fo.toFixed(4)};${e.toFixed(4)};1`,
      values: `0;0;0.95;0.95;0;0`,
    };
  };

  // Inizio pulse per layer
  const layerStart = [0.1, 0.9, 1.7];

  const renderNode = (x, y, fill, glowBeginTime, glowColor) => (
    <g key={`${x}-${y}`}>
      {/* Anello esterno: si espande all'arrivo */}
      <circle cx={x} cy={y} r={R + 2} fill="none" stroke={glowColor} strokeWidth="1.5">
        <animate attributeName="opacity"
          values="0;0;0.6;0"
          keyTimes={`0;${(parseFloat(glowBeginTime) / CYCLE).toFixed(4)};${Math.min((parseFloat(glowBeginTime) / CYCLE + 0.08), 1).toFixed(4)};1`}
          dur={`${CYCLE}s`} repeatCount="indefinite" />
        <animate attributeName="r"
          values={`${R};${R};${R + 10};${R + 16}`}
          keyTimes={`0;${(parseFloat(glowBeginTime) / CYCLE).toFixed(4)};${Math.min((parseFloat(glowBeginTime) / CYCLE + 0.08), 1).toFixed(4)};1`}
          dur={`${CYCLE}s`} repeatCount="indefinite" />
      </circle>
      {/* Nodo principale */}
      <circle cx={x} cy={y} r={R} fill={fill}>
        <animate attributeName="r"
          values={`${R};${R};${R + 4};${R}`}
          keyTimes={`0;${(parseFloat(glowBeginTime) / CYCLE).toFixed(4)};${Math.min((parseFloat(glowBeginTime) / CYCLE + 0.06), 1).toFixed(4)};1`}
          dur={`${CYCLE}s`} repeatCount="indefinite" />
      </circle>
    </g>
  );

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: 480, display: 'block' }}>
      <defs>
        {edges.map(e => (
          <path key={`def-${e.id}`} id={`ep-${e.id}`}
            d={`M${e.x1},${e.y1} L${e.x2},${e.y2}`} />
        ))}
      </defs>

      {/* Connessioni tratteggiate */}
      {edges.map(e => (
        <line key={`ln-${e.id}`}
          x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2}
          stroke="rgba(180,200,255,0.18)" strokeWidth="1.2" strokeDasharray="5,4" />
      ))}

      {/* Pulse lungo ogni edge */}
      {edges.map(e => {
        const startT = layerStart[e.layer];
        const { keyTimes: kt, keyPoints: kp } = pulseKeys(startT);
        const { keyTimes: okt, values: ov } = pulseOpacity(startT);
        const pCol = e.layer === 0 ? COL_Y : e.layer === 1 ? COL_B : COL_G;
        return (
          <circle key={`pulse-${e.id}`} r={PR} fill={pCol}>
            <animateMotion dur={`${CYCLE}s`} repeatCount="indefinite"
              calcMode="linear" keyTimes={kt} keyPoints={kp}>
              <mpath href={`#ep-${e.id}`} />
            </animateMotion>
            <animate attributeName="opacity" dur={`${CYCLE}s`}
              repeatCount="indefinite" keyTimes={okt} values={ov} />
          </circle>
        );
      })}

      {/* Nodi input (gialli) — si attivano per primi */}
      {inp.map(([x, y], i) => renderNode(x, y, COL_Y, '0.05', COL_Y))}

      {/* Nodi H1 (blu) */}
      {h1.map(([x, y], i) => renderNode(x, y, COL_B, `${layerStart[0] + PULSE_DUR}`, COL_B))}

      {/* Nodi H2 (blu) */}
      {h2.map(([x, y], i) => renderNode(x, y, COL_B, `${layerStart[1] + PULSE_DUR}`, COL_B))}

      {/* Output (verde) */}
      {out.map(([x, y], i) => renderNode(x, y, COL_G, `${layerStart[2] + PULSE_DUR}`, COL_G))}
    </svg>
  );
}

const Predictions = () => {
  const [forecasts, setForecasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cutoffDate, setCutoffDate] = useState('');
  const [latestDateGlobal, setLatestDateGlobal] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [executingPortfolio, setExecutingPortfolio] = useState(null);
  const [strategyStatusByPortfolio, setStrategyStatusByPortfolio] = useState({});
  const [lockedStrategies, setLockedStrategies] = useState({});
  const [confirmModal, setConfirmModal] = useState(false);
  const [showWeekWarning, setShowWeekWarning] = useState(false);
  const [globalExecuting, setGlobalExecuting] = useState(false);
  const [autopilot, setAutopilot] = useState(false);
  const [autopilotRunning, setAutopilotRunning] = useState(false);
  const [marketClock, setMarketClock] = useState(null);

  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const isRequestRunning = useRef(false);
  const autopilotRef = useRef(false);
  const { t } = useTranslation();

  // Avviso late-week: scatta quando il caricamento finisce
  useEffect(() => {
    if (loading) return;
    if (autopilot) return;
    const day = new Date().getDay(); // 0=dom 1=lun 2=mar 3=mer 4=gio 5=ven 6=sab
    if (![0, 3, 4, 5, 6].includes(day)) return;
    const statuses = Object.values(strategyStatusByPortfolio);
    if (statuses.length === 0) return;
    const allExecuted = statuses.every(s => s.reason === 'already_executed');
    if (!allExecuted) setShowWeekWarning(true);
  }, [loading]); // eslint-disable-line

  // ---------------------------------------------------------
  // Utils
  // ---------------------------------------------------------
  const calculateLastMonday = () => {
    const today = new Date();
    const lastMonday = new Date(today);
    lastMonday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    return lastMonday.toLocaleDateString();
  };

  const formatPrice = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? '—' : n.toFixed(2);
  };

  const getSideFromQuantity = (q) => {
    if (q > 0) return 'BUY';
    if (q < 0) return 'SELL';
    return 'HOLD';
  };

  const getSideColor = (side) => {
    if (side.startsWith('BUY')) return SEMANTIC.actionBuy;
    if (side.startsWith('SELL')) return SEMANTIC.actionSell;
    if (side.includes('FAILED')) return SEMANTIC.textDisabled;
    return HOLD_COLOR;
  };

  const getSideBorder = (side) => {
    if (side.startsWith('BUY')) return SEMANTIC.borderBuy;
    if (side.startsWith('SELL')) return SEMANTIC.borderSell;
    if (side.includes('FAILED')) return SEMANTIC.textDisabled;
    return HOLD_COLOR;
  };

  // ── Autopilot view: activity list ────────────────────────
  const renderAutopilotList = (recommendations) => (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {recommendations.map(r => {
        const isExecuted = r.side === 'HOLD' && r.originalSide;
        const pendingDir = r.isPending
          ? (r.originalSide || r.side.split(' - ')[0]).toUpperCase()
          : null;
        const pendingLabel = pendingDir === 'BUY'  ? 'Acquisto in attesa'
                           : pendingDir === 'SELL' ? 'Vendita in attesa'
                           : null;
        const pendingColor = pendingDir === 'BUY'  ? SEMANTIC.actionBuy
                           : pendingDir === 'SELL' ? SEMANTIC.actionSell
                           : '#00aaff';
        return (
          <li key={`ap-${r.ticker}`} style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '11px 14px',
            backgroundColor: SEMANTIC.backgroundCard,
            borderRadius: UI.borderRadius.md,
            boxShadow: UI.shadow.soft,
          }}>
            <img
              src={`${process.env.PUBLIC_URL}/logos/${r.ticker}.png`}
              alt={r.ticker}
              style={{ width: 38, height: 38, borderRadius: 9, backgroundColor: '#fff', objectFit: 'contain', flexShrink: 0 }}
              onError={e => { e.target.src = `${process.env.PUBLIC_URL}/logos/default-logo.png`; }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: UI.fontWeight.bold, fontSize: UI.fontSize.md }}>{r.ticker}</div>
              <div style={{ fontSize: UI.fontSize.xs, color: SEMANTIC.textSecondary, marginTop: 2 }}>
                × {Math.abs(Number(r.quantity) || 0)} azioni · ${formatPrice(r.price)}
              </div>
              {pendingLabel && (
                <div style={{ fontSize: UI.fontSize.xs, color: pendingColor, marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <i className="bi bi-clock-fill" style={{ fontSize: 11 }} />
                  {pendingLabel}
                  {marketClock && !marketClock.is_open && (
                    <span style={{ color: SEMANTIC.textSecondary, opacity: 0.7 }}>· all'apertura</span>
                  )}
                </div>
              )}
            </div>
            {isExecuted && <i className="bi bi-check-circle-fill" style={{ color: SEMANTIC.actionBuy,  fontSize: 15, flexShrink: 0 }} />}
            {r.isPending  && <i className="bi bi-clock-fill"       style={{ color: pendingColor,        fontSize: 15, flexShrink: 0 }} />}
            {r.isFailed   && <i className="bi bi-x-circle-fill"    style={{ color: SEMANTIC.actionSell, fontSize: 15, flexShrink: 0 }} />}
          </li>
        );
      })}
    </ul>
  );

  // ---------------------------------------------------------
  // Overlay ordini eseguiti sulle raccomandazioni
  // ---------------------------------------------------------
  const overlayOrderStatus = (recommendations, strategyStatus) => {
    if (!strategyStatus || strategyStatus.reason !== 'already_executed') {
      return recommendations;
    }

    const orderDetails = strategyStatus.order_details || [];
    const allFilled = strategyStatus.all_filled;

    const orderMap = {};
    for (const o of orderDetails) {
      orderMap[o.symbol.toUpperCase()] = o;
    }

    return recommendations.map((r) => {
      const order = orderMap[r.ticker];

      if (allFilled) {
        return { ...r, side: 'HOLD', originalSide: r.side };
      }

      if (order) {
        const isPending = ['created', 'submitted', 'queued', 'new', 'partial'].includes(order.status);

        if (order.status === 'filled') {
          return { ...r, side: 'HOLD', originalSide: r.side };
        }

        if (isPending) {
          const pendingSide = order.side.toUpperCase();
          return {
            ...r,
            side: `${pendingSide} - PENDING`,
            originalSide: r.side,
            isPending: true,
          };
        }

        return { ...r, side: `${r.side} - FAILED`, originalSide: r.side, isFailed: true };
      }

      return { ...r, side: 'HOLD', originalSide: r.side };
    });
  };

  // ---------------------------------------------------------
  // Strategy status
  // ---------------------------------------------------------
  const fetchStrategyStatus = useCallback(async (portfolioId) => {
    const res = await API.get(`/strategy/status/${portfolioId}`);
    setStrategyStatusByPortfolio((prev) => ({
      ...prev,
      [portfolioId]: res.data,
    }));
  }, []);

  // ---------------------------------------------------------
  // Fetch portfolios
  // ---------------------------------------------------------
  const fetchUserPortfolios = useCallback(async () => {
    const res = await API.get(`/user/${userId}/portfolios-with-models`);
    return res.data.portfolios || [];
  }, [userId]);

  // ---------------------------------------------------------
  // Holdings fetch
  // ---------------------------------------------------------
  const normalizeHoldings = (data) => {
    const candidates =
      data?.tickers ||
      data?.holdings ||
      data?.portfolio?.tickers ||
      data?.portfolio_tickers ||
      (Array.isArray(data) ? data : null);

    const arr = Array.isArray(candidates) ? candidates : [];
    return arr
      .map((row) => ({
        ticker:
          row?.ticker_symbol ||
          row?.ticker ||
          row?.symbol ||
          row?.tickerSymbol ||
          row?.tickerSymbol?.toString?.(),
        quantity: row?.quantity ?? row?.qty ?? row?.shares ?? 0,
      }))
      .filter((x) => typeof x.ticker === 'string' && x.ticker.trim().length > 0)
      .map((x) => ({ ...x, ticker: x.ticker.trim().toUpperCase() }));
  };

  const fetchPortfolioHoldings = useCallback(async (portfolioId) => {
    const endpointsToTry = [
      `/portfolio/${portfolioId}/tickers`,
    ];

    for (const ep of endpointsToTry) {
      try {
        const res = await API.get(ep);
        const holdings = normalizeHoldings(res.data);
        if (holdings.length) return holdings;
      } catch (e) {
        // continua
      }
    }

    return [];
  }, []);

  // ---------------------------------------------------------
  // Forecast runner
  // ---------------------------------------------------------
  const runForecastsForAllPortfolios = useCallback(
    async (portfolios) => {
      if (isRequestRunning.current) return;
      isRequestRunning.current = true;

      setLoading(true);
      setForecasts([]);

      try {
        const all = [];
        const collectedStatuses = {};
        let latest = null;

        for (let i = 0; i < portfolios.length; i++) {
          const { portfolio_id, model_id, model_name } = portfolios[i];
          await API.post('/run-forecast', { portfolioId: portfolio_id });

          const recRes = await API.get(
            `/portfolio/${portfolio_id}/predictions/latest?model_id=${model_id}`
          );

          const baseRecommendations = (recRes.data.recommendations || []).map((r) => {
            const quantity = r[1];
            const side = getSideFromQuantity(quantity);
            return {
              ticker: r[0],
              quantity,
              price: r[2],
              valid_from: new Date(r[3]),
              side,
            };
          });

          const holdings = await fetchPortfolioHoldings(portfolio_id);
          const predictedSet = new Set(baseRecommendations.map((x) => x.ticker));

          const sellTiles = holdings
            .filter((h) => !predictedSet.has(h.ticker))
            .map((h) => ({
              ticker: h.ticker,
              quantity: Number(h.quantity) || 0,
              price: null,
              valid_from: baseRecommendations[0]?.valid_from
                ? new Date(baseRecommendations[0].valid_from)
                : new Date(),
              side: 'SELL',
            }));

          const recommendations = [...baseRecommendations, ...sellTiles];

          if (recommendations.length) {
            const d = recommendations.reduce(
              (a, b) => (b.valid_from > a ? b.valid_from : a),
              new Date(0)
            );
            if (!latest || d > latest) latest = d;
          }

          const statusRes = await API.get(`/strategy/status/${portfolio_id}`);
          const strategyStatus = statusRes.data;

          collectedStatuses[portfolio_id] = strategyStatus;
          setStrategyStatusByPortfolio((prev) => ({
            ...prev,
            [portfolio_id]: strategyStatus,
          }));

          const finalRecommendations = overlayOrderStatus(recommendations, strategyStatus);

          all.push({
            portfolio_id,
            model_id,
            model_name,
            recommendations: finalRecommendations,
          });
        }

        setForecasts(all);
        setLatestDateGlobal(latest ? latest.toLocaleDateString() : '');
        return collectedStatuses;
      } finally {
        setLoading(false);
        isRequestRunning.current = false;
      }
    },
    [fetchPortfolioHoldings]
  );

  // ---------------------------------------------------------
  // Init
  // ---------------------------------------------------------
  useEffect(() => {
    const init = async () => {
      if (!userId) return navigate('/');
      setCutoffDate(calculateLastMonday());

      // Carica impostazione autopilot e stato mercato
      try {
        const [profileRes, clockRes] = await Promise.all([
          API.get(`/users/${userId}/profile`),
          API.get('/market/clock').catch(() => null),
        ]);
        const isAutopilot = !!profileRes.data.autopilot;
        setAutopilot(isAutopilot);
        autopilotRef.current = isAutopilot;
        if (clockRes) setMarketClock(clockRes.data);
      } catch { /* silent */ }

      const portfolios = await fetchUserPortfolios();
      const statuses = await runForecastsForAllPortfolios(portfolios);

      // Autopilot: esecuzione automatica per portafogli eleggibili
      if (autopilotRef.current && statuses) {
        setAutopilotRunning(true);
        try {
          for (const p of portfolios) {
            const st = statuses[p.portfolio_id] ?? {};
            const canExec = st.can_execute !== false
              && !st.has_pending
              && st.reason !== 'already_executed';
            if (canExec) {
              await doExecuteStrategy(p.portfolio_id, p.model_id); // eslint-disable-line
            }
          }
        } finally {
          setAutopilotRunning(false);
        }
      }
    };
    init();
  }, [userId, navigate, fetchUserPortfolios, runForecastsForAllPortfolios]); // eslint-disable-line

  // ---------------------------------------------------------
  // Execute
  // ---------------------------------------------------------
  const _runSinglePortfolio = async (portfolioId, modelId) => {
    setExecutingPortfolio(portfolioId);
    try {
      const res = await API.post('/strategy/execute', {
        portfolio_id: portfolioId,
        model_id: modelId,
        confirm: true,
      });
      if (res.data?.mode === 'submitted' || res.data?.mode === 'blocked') {
        setLockedStrategies((prev) => ({ ...prev, [portfolioId]: true }));
      }
      await fetchStrategyStatus(portfolioId);
    } finally {
      setExecutingPortfolio(null);
    }
  };

  // kept for autopilot compatibility
  const doExecuteStrategy = _runSinglePortfolio;

  const doExecuteAll = async () => {
    setConfirmModal(false);
    setGlobalExecuting(true);
    try {
      for (const f of forecasts) {
        const status = strategyStatusByPortfolio[f.portfolio_id] ?? {};
        const canRun = !lockedStrategies[f.portfolio_id]
          && !status.has_pending
          && (status.pending_orders ?? 0) === 0
          && status.can_execute !== false
          && status.reason !== 'already_executed';
        if (canRun) {
          await _runSinglePortfolio(f.portfolio_id, f.model_id);
        }
      }
    } finally {
      setGlobalExecuting(false);
    }
  };

  // ---------------------------------------------------------
  // Badge contestuale in base allo stato degli ordini
  // ---------------------------------------------------------
  // eslint-disable-next-line no-unused-vars
  const StatusBadge = ({ status }) => {
    const isPending = status.has_pending ||
      (status.reason === 'pending_orders' && status.pending_orders > 0);
    const isExecuted = status.reason === 'already_executed';

    const pendingCount = isPending
      ? (status.order_details || []).filter(
          (o) => ['created', 'submitted', 'queued', 'new', 'partial'].includes(o.status)
        ).length || status.pending_orders || 0
      : 0;

    let badgeText = t('predictions.manual_only');
    let badgeBg = SEMANTIC.backgroundDisabled;
    let badgeColor = SEMANTIC.textPrimary;

    if (isPending) {
      badgeText = t('predictions.orders_pending', { count: pendingCount });
      badgeBg = SEMANTIC.actionBuy;
      badgeColor = SEMANTIC.ctaPrimaryText;
    } else if (isExecuted) {
      badgeText = t('predictions.strategy_executed');
      badgeBg = SEMANTIC.actionBuy;
      badgeColor = SEMANTIC.ctaPrimaryText;
    }

    return (
      <span
        style={{
          backgroundColor: badgeBg,
          color: badgeColor,
          padding: '6px 14px',
          borderRadius: UI.borderRadius.pill,
          fontWeight: UI.fontWeight.bold,
          fontSize: UI.fontSize.sm,
        }}
      >
        {badgeText}
      </span>
    );
  };

  // ---------------------------------------------------------
  // LOADING SCREEN
  // ---------------------------------------------------------
  if (loading) {
    return (
      <div style={{
        backgroundColor: SEMANTIC.backgroundPage,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: SEMANTIC.textPrimary,
        gap: 0,
      }}>
        <NeuralNetLoader />
        <p style={{
          marginTop: 24,
          fontSize: 16,
          fontWeight: 600,
          color: SEMANTIC.textSecondary,
          letterSpacing: '0.03em',
        }}>
          Attendere, elaborazione in corso…
        </p>
      </div>
    );
  }

  // ---------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------
  return (
    <div
      style={{
        backgroundColor: SEMANTIC.backgroundPage,
        minHeight: '100vh',
        padding: UI.spacing.xl,
        color: SEMANTIC.textPrimary,
      }}
    >
      {/* ── Modale avviso late-week ── */}
      {showWeekWarning && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1100,
            backgroundColor: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: UI.spacing.lg,
          }}
        >
          <div
            style={{
              backgroundColor: SEMANTIC.backgroundCard,
              borderRadius: UI.borderRadius.lg,
              border: '1px solid rgba(255,170,0,0.35)',
              boxShadow: '0 0 40px rgba(255,170,0,0.15)',
              padding: '32px 28px',
              maxWidth: '420px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: UI.spacing.lg,
            }}
          >
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              backgroundColor: 'rgba(255,170,0,0.12)',
              border: '2px solid rgba(255,170,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className="bi bi-calendar-week" style={{ fontSize: 24, color: '#ffaa00' }} />
            </div>

            <p style={{
              color: SEMANTIC.textPrimary,
              fontSize: UI.fontSize.md,
              margin: 0,
              textAlign: 'center',
              lineHeight: '1.7',
            }}>
              {t('predictions.late_week_warning')}
            </p>

            <button
              onClick={() => setShowWeekWarning(false)}
              style={{
                width: '100%',
                padding: UI.spacing.md,
                backgroundColor: SEMANTIC.ctaPrimary,
                border: 'none',
                borderRadius: UI.borderRadius.pill,
                color: SEMANTIC.ctaPrimaryText,
                fontSize: UI.fontSize.md,
                fontWeight: UI.fontWeight.bold,
                cursor: 'pointer',
              }}
            >
              {t('predictions.late_week_warning_btn')}
            </button>
          </div>
        </div>
      )}

      {/* ── Modale conferma esecuzione globale ── */}
      {confirmModal && (
        <div
          onClick={() => setConfirmModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            backgroundColor: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: UI.spacing.lg,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: SEMANTIC.backgroundCard,
              borderRadius: UI.borderRadius.lg,
              border: `1px solid ${SEMANTIC.ctaPrimary}44`,
              boxShadow: `0 0 40px ${SEMANTIC.ctaPrimary}22`,
              padding: '32px 28px',
              maxWidth: '420px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: UI.spacing.lg,
            }}
          >
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              backgroundColor: `${SEMANTIC.ctaPrimary}18`,
              border: `2px solid ${SEMANTIC.ctaPrimary}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className="bi bi-lightning-charge-fill" style={{ fontSize: '24px', color: SEMANTIC.ctaPrimary }} />
            </div>

            <h3 style={{ color: SEMANTIC.textPrimary, fontSize: UI.fontSize.xl, fontWeight: UI.fontWeight.bold, margin: 0, textAlign: 'center' }}>
              {t('predictions.confirm_execute')}
            </h3>

            <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.md, margin: 0, textAlign: 'center', lineHeight: '1.6' }}>
              {t('predictions.confirm_execute_detail')}
            </p>

            <div style={{ display: 'flex', gap: UI.spacing.md, width: '100%', marginTop: UI.spacing.sm }}>
              <button
                onClick={() => setConfirmModal(false)}
                style={{
                  flex: 1, padding: UI.spacing.md,
                  backgroundColor: 'transparent',
                  border: `2px solid rgba(255,255,255,0.2)`,
                  borderRadius: UI.borderRadius.pill,
                  color: SEMANTIC.textSecondary,
                  fontSize: UI.fontSize.md, fontWeight: UI.fontWeight.bold,
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={doExecuteAll}
                style={{
                  flex: 1, padding: UI.spacing.md,
                  backgroundColor: SEMANTIC.ctaPrimary,
                  border: 'none',
                  borderRadius: UI.borderRadius.pill,
                  color: SEMANTIC.ctaPrimaryText,
                  fontSize: UI.fontSize.md, fontWeight: UI.fontWeight.bold,
                  cursor: 'pointer',
                }}
              >
                {t('predictions.confirm_execute_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      <BackButton />

      {/* ── Banner Autopilot ── */}
      {autopilot ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          backgroundColor: autopilotRunning ? 'rgba(0,170,255,0.1)' : 'rgba(0,255,136,0.07)',
          border: `1px solid ${autopilotRunning ? 'rgba(0,170,255,0.4)' : 'rgba(0,255,136,0.3)'}`,
          borderRadius: 10, padding: '10px 16px', marginBottom: 16,
        }}>
          <span style={{ fontSize: 18 }}>✈️</span>
          <div>
            <div style={{ color: autopilotRunning ? '#00aaff' : SEMANTIC.actionBuy, fontWeight: 700, fontSize: 13 }}>
              {autopilotRunning ? 'Autopilot in esecuzione…' : 'Autopilot attivo'}
            </div>
            <div style={{ color: SEMANTIC.textSecondary, fontSize: 11 }}>
              {autopilotRunning
                ? 'La strategia sta venendo eseguita automaticamente sui tuoi portafogli.'
                : 'La strategia è stata eseguita automaticamente.'}
            </div>
          </div>
        </div>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          backgroundColor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 10, padding: '10px 14px', marginBottom: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>✈️</span>
            <div>
              <div style={{ color: SEMANTIC.textSecondary, fontWeight: 700, fontSize: 13 }}>
                Autopilot non attivo
              </div>
              <div style={{ color: SEMANTIC.textSecondary, fontSize: 11, opacity: 0.7 }}>
                La strategia deve essere eseguita manualmente.
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/settings')}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid rgba(255,255,255,0.2)`,
              borderRadius: UI.borderRadius.pill,
              color: SEMANTIC.textSecondary,
              fontSize: UI.fontSize.sm,
              fontWeight: UI.fontWeight.bold,
              padding: '6px 14px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Attiva
          </button>
        </div>
      )}

      <h2 className="text-center">{t('predictions.title', { count: forecasts.length })}</h2>
      <p className="text-center" style={{ color: SEMANTIC.textSecondary }}>
        {t('predictions.subtitle', { from: cutoffDate, to: latestDateGlobal || '—' })}
      </p>

      {/* ── Market clock ── */}
      {marketClock && (() => {
        const isOpen = marketClock.is_open;
        const color  = isOpen ? SEMANTIC.actionBuy : '#ffaa00';
        const nextDt = new Date(isOpen ? marketClock.next_close : marketClock.next_open);
        const timeStr = nextDt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const dateStr = nextDt.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
        const isToday = nextDt.toDateString() === new Date().toDateString();
        const subLabel = isOpen
          ? t('predictions.market_closes_at', { time: timeStr })
          : isToday
            ? t('predictions.market_opens_at', { date: t('common.today', 'today'), time: timeStr })
            : t('predictions.market_opens_at', { date: dateStr, time: timeStr });
        return (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, marginBottom: 24,
          }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '7px 16px', borderRadius: 24,
              backgroundColor: `${color}12`,
              border: `1px solid ${color}44`,
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                backgroundColor: color,
                boxShadow: isOpen ? `0 0 6px ${color}` : 'none',
                flexShrink: 0,
                animation: isOpen ? 'market-pulse 2s ease-in-out infinite' : 'none',
              }} />
              <span style={{ color, fontWeight: 700, fontSize: 13 }}>
                {isOpen ? t('predictions.market_open') : t('predictions.market_closed')}
              </span>
              <span style={{ color: SEMANTIC.textSecondary, fontSize: 12 }}>
                {subLabel}
              </span>
            </div>
          </div>
        );
      })()}

      <style>{`
        @keyframes market-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      {/* ── Barra azione globale ── */}
      {forecasts.length > 0 && (() => {
        const canExecuteAny = forecasts.some(f => {
          const st = strategyStatusByPortfolio[f.portfolio_id] ?? {};
          return !lockedStrategies[f.portfolio_id]
            && !st.has_pending
            && (st.pending_orders ?? 0) === 0
            && st.can_execute !== false
            && st.reason !== 'already_executed';
        });
        const hasPending = forecasts.some(f => {
          const st = strategyStatusByPortfolio[f.portfolio_id] ?? {};
          return st.has_pending || (st.pending_orders ?? 0) > 0;
        });

        return (
          <div style={{
            maxWidth: 520, margin: '0 auto 32px',
            backgroundColor: SEMANTIC.backgroundCard,
            borderRadius: UI.borderRadius.lg,
            padding: '20px 24px',
            boxShadow: UI.shadow.card,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
          }}>
            <div>
              <div style={{ color: SEMANTIC.textPrimary, fontWeight: UI.fontWeight.bold, fontSize: UI.fontSize.md }}>
                {t('predictions.strategy_title')}
              </div>
              <div style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, marginTop: 4 }}>
                {forecasts.length === 1
                  ? t('predictions.portfolio_count_one')
                  : t('predictions.portfolio_count', { count: forecasts.length })}
              </div>
            </div>

            {globalExecuting || autopilotRunning ? (
              <span style={{
                display: 'flex', alignItems: 'center', gap: 8,
                color: SEMANTIC.actionBuy, fontWeight: UI.fontWeight.bold, fontSize: UI.fontSize.sm,
              }}>
                <i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite', fontSize: 16 }} />
                {t('predictions.executing')}
              </span>
            ) : canExecuteAny ? (
              <button
                onClick={() => setConfirmModal(true)}
                style={{
                  backgroundColor: SEMANTIC.ctaPrimary,
                  color: SEMANTIC.ctaPrimaryText,
                  borderRadius: UI.borderRadius.pill,
                  padding: '10px 24px',
                  border: 'none',
                  fontWeight: UI.fontWeight.bold,
                  fontSize: UI.fontSize.md,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                  whiteSpace: 'nowrap',
                }}
              >
                <i className="bi bi-lightning-charge-fill" />
                {t('predictions.execute')}
              </button>
            ) : hasPending ? (
              <span style={{
                backgroundColor: `${SEMANTIC.actionBuy}22`,
                color: SEMANTIC.actionBuy,
                border: `1px solid ${SEMANTIC.actionBuy}66`,
                padding: '8px 16px',
                borderRadius: UI.borderRadius.pill,
                fontWeight: UI.fontWeight.bold,
                fontSize: UI.fontSize.sm,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <i className="bi bi-clock-history" />
                {t('predictions.orders_pending', { count: '' }).trim()}
              </span>
            ) : (
              <span style={{
                backgroundColor: `${SEMANTIC.actionBuy}22`,
                color: SEMANTIC.actionBuy,
                border: `1px solid ${SEMANTIC.actionBuy}66`,
                padding: '8px 16px',
                borderRadius: UI.borderRadius.pill,
                fontWeight: UI.fontWeight.bold,
                fontSize: UI.fontSize.sm,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <i className="bi bi-check-circle-fill" />
                {t('predictions.strategy_executed')}
              </span>
            )}
          </div>
        );
      })()}

      {forecasts.map((f) => {
        const status = strategyStatusByPortfolio[f.portfolio_id] ?? {
          can_execute: true,
          pending_orders: 0,
        };

        return (
          <div key={f.portfolio_id} style={{ marginBottom: 50 }}>
            <h3 className="text-center">{t('predictions.portfolio_label', { name: f.model_name })}</h3>

            <div
              style={{
                backgroundColor: SEMANTIC.backgroundCard,
                borderRadius: UI.borderRadius.lg,
                padding: UI.spacing.xl,
                maxWidth: 520,
                margin: '0 auto',
                boxShadow: UI.shadow.card,
              }}
            >
              {/* GRID / autopilot view */}
              {(autopilot || status.reason === 'already_executed') ? renderAutopilotList(f.recommendations) : (
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: UI.spacing.md,
                  }}
                >
                  {f.recommendations.map((r) => {
                    const color = getSideColor(r.side);
                    const border = getSideBorder(r.side);

                    return (
                      <li
                        key={`${r.side}-${r.ticker}`}
                        style={{
                          display: 'flex',
                          gap: UI.spacing.md,
                          alignItems: 'center',
                          padding: UI.spacing.md,
                          backgroundColor: SEMANTIC.backgroundCard,
                          borderRadius: UI.borderRadius.md,
                          border: `1px solid ${border}`,
                        }}
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}/logos/${r.ticker}.png`}
                          alt={r.ticker}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: UI.borderRadius.sm,
                            backgroundColor: '#fff',
                            objectFit: 'contain',
                          }}
                          onError={(e) => {
                            e.target.src = `${process.env.PUBLIC_URL}/logos/default-logo.png`;
                          }}
                        />
                        <div>
                          <strong>{r.ticker}</strong>
                          <div
                            style={{
                              fontSize: UI.fontSize.sm,
                              fontWeight: UI.fontWeight.bold,
                              color,
                              opacity: r.isPending ? 0.8 : 1,
                            }}
                          >
                            {r.side}
                          </div>
                          <div style={{ fontSize: UI.fontSize.sm, opacity: 0.85 }}>
                            Qty: {Math.abs(Number(r.quantity) || 0)} · $
                            {formatPrice(r.price)}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Predictions;
